import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { FiTarget, FiEye, FiAward, FiUsers, FiCheck } from "react-icons/fi";

export const About = (props) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const whyList = props?.data?.Why || [
    "Uzman ve deneyimli kadro",
    "Bireysel yaklaşım",
    "Bilimsel yöntemler",
    "Aile odaklı çalışma",
  ];
  const why2List = props?.data?.Why2 || [
    "Güvenli ortam",
    "Esnek randevu saatleri",
    "Sürekli iletişim",
    "Takip ve değerlendirme",
  ];
  const seo = props?.data?.seo || {};

  const stats = [
    { icon: FiUsers, value: "500+", label: "Mutlu Aile", color: "#3b82f6" },
    { icon: FiAward, value: "5+", label: "Yıl Tecrübe", color: "#10b981" },
    { icon: FiUsers, value: "1000+", label: "Danışmanlık", color: "#8b5cf6" },
    { icon: FiAward, value: "98%", label: "Memnuniyet", color: "#f59e0b" },
  ];

  // Schema.org JSON-LD for About Page
  const schemaOrgJSONLD = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    mainEntity: {
      "@type": "ProfessionalService",
      name: "Dilan Aslan | Çocuk Gelişim Danışmanlığı",
      description:
        "Mardin Artuklu'da 10+ yıllık tecrübe ile çocuk gelişim danışmanlığı, oyun terapisi ve ebeveyn danışmanlığı hizmetleri sunan uzman merkez.",
      image: "https://www.dilanaslan.com/img/intro-bg11.jpg",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Artuklu",
        addressLocality: "Mardin",
        addressRegion: "Mardin",
        postalCode: "47200",
        addressCountry: "TR",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: "37.3212",
        longitude: "40.7245",
      },
      telephone: "+905055517747",
      url: "https://www.dilanaslan.com",
      foundingDate: "2014",
      founder: {
        "@type": "Person",
        name: "Dilan Aslan",
        jobTitle: "Çocuk Gelişim Uzmanı",
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "500",
        bestRating: "5",
        worstRating: "1",
      },
      areaServed: {
        "@type": "City",
        name: "Mardin",
      },
      knowsAbout: [
        "Çocuk Gelişimi",
        "Oyun Terapisi",
        "Ebeveyn Danışmanlığı",
        "Gelişim Değerlendirmesi",
        "Çocuk Psikolojisi",
      ],
      sameAs: ["https://www.instagram.com/cg.dilanaslan/"],
    },
  };

  return (
    <section
      id="about"
      style={{
        padding: isMobile ? "60px 0" : "80px 0",
        background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Dekoratif Arka Plan */}
      <div
        style={{
          position: "absolute",
          top: "-200px",
          right: "-200px",
          width: "500px",
          height: "500px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-150px",
          left: "-150px",
          width: "400px",
          height: "400px",
          background:
            "radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 70%)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      <Helmet>
        {/* Temel Meta Etiketleri */}
        <html lang="tr" />
        <title>
          {seo.title ||
            "Hakkımızda | Dilan Aslan Çocuk Gelişim Danışmanlığı - Mardin Artuklu"}
        </title>
        <meta
          name="description"
          content={
            seo.description ||
            "Mardin Artuklu'da 10+ yıllık tecrübe ile çocuk gelişim danışmanlığı. Uzman Dilan Aslan ve ekibi ile çocuğunuzun sağlıklı gelişimi için profesyonel destek. 500+ mutlu aile, %98 memnuniyet oranı."
          }
        />
        <meta
          name="keywords"
          content="Dilan Aslan hakkında, çocuk gelişim uzmanı Mardin, Artuklu çocuk danışmanı, oyun terapisi uzmanı, ebeveyn danışmanlığı Mardin, çocuk gelişim merkezi, profesyonel çocuk danışmanlığı, Mardin pedagog"
        />
        <meta name="author" content="Dilan Aslan" />
        <link rel="canonical" href="https://www.dilanaslan.com/hakkimizda" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.dilanaslan.com/hakkimizda"
        />
        <meta
          property="og:title"
          content="Hakkımızda | Dilan Aslan Çocuk Gelişim Danışmanlığı"
        />
        <meta
          property="og:description"
          content="Mardin Artuklu'da 10+ yıllık tecrübe ile çocuk gelişim danışmanlığı. 500+ mutlu aile, %98 memnuniyet oranı ile profesyonel destek."
        />
        <meta
          property="og:image"
          content="https://www.dilanaslan.com/img/intro-bg11.jpg"
        />
        <meta property="og:locale" content="tr_TR" />
        <meta
          property="og:site_name"
          content="Dilan Aslan Çocuk Gelişim Danışmanlığı"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:url"
          content="https://www.dilanaslan.com/hakkimizda"
        />
        <meta
          name="twitter:title"
          content="Hakkımızda | Dilan Aslan Çocuk Gelişim Danışmanlığı"
        />
        <meta
          name="twitter:description"
          content="Mardin Artuklu'da 10+ yıllık tecrübe ile çocuk gelişim danışmanlığı. Profesyonel destek ve uzman kadro."
        />
        <meta
          name="twitter:image"
          content="https://www.dilanaslan.com/img/intro-bg11.jpg"
        />

        {/* Coğrafi Etiketler */}
        <meta name="geo.region" content="TR-47" />
        <meta name="geo.placename" content="Mardin, Artuklu" />
        <meta name="geo.position" content="37.3212;40.7245" />
        <meta name="ICBM" content="37.3212, 40.7245" />

        {/* Mobil Optimizasyon */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#2563EB" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black-translucent"
        />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgJSONLD)}
        </script>

        {/* Robots */}
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <meta name="googlebot" content="index, follow" />
      </Helmet>

      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: isMobile ? "0 16px" : "0 24px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Başlık */}
        <motion.div
          style={{
            textAlign: "center",
            marginBottom: isMobile ? "32px" : "40px",
          }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span
            style={{
              display: "inline-block",
              backgroundColor: "#dbeafe",
              color: "#1d4ed8",
              padding: "8px 20px",
              borderRadius: "50px",
              fontSize: "0.9rem",
              fontWeight: "600",
              marginBottom: "12px",
            }}
          >
            BİZİ TANIYIN
          </span>
          <h2
            style={{
              fontSize: isMobile ? "1.75rem" : "2.5rem",
              fontWeight: "800",
              color: "#0f172a",
              marginBottom: "12px",
              lineHeight: "1.2",
            }}
          >
            Misyon & Vizyonumuz
          </h2>
          <p
            style={{
              fontSize: isMobile ? "1rem" : "1.15rem",
              color: "#64748b",
              maxWidth: "600px",
              margin: "0 auto",
              lineHeight: "1.6",
            }}
          >
            Çocukların sağlıklı gelişimi için profesyonel destek sunuyoruz
          </p>
        </motion.div>

        {/* Video ve İçerik Grid */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "24px" : "32px",
            alignItems: "start",
            marginBottom: isMobile ? "32px" : "48px",
          }}
        >
          {/* Video Bölümü - Dikey */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{
              position: "relative",
              width: isMobile ? "100%" : "300px",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: "relative",
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              }}
            >
              <video
                style={{
                  width: "100%",
                  height: isMobile ? "400px" : "500px",
                  objectFit: "cover",
                  display: "block",
                  borderRadius: "20px",
                }}
                autoPlay
                muted
                loop
                playsInline
                controls
              >
                <source src="/video/video.mp4" type="video/mp4" />
                Tarayıcınız video etiketini desteklemiyor.
              </video>
            </div>
          </motion.div>

          {/* Misyon & Vizyon Kartları + Neden Bizi Seçmelisiniz */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              flex: 1,
              width: "100%",
            }}
          >
            {/* Misyon Kartı */}
            <div
              style={{
                background: "linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)",
                borderRadius: "16px",
                padding: isMobile ? "20px" : "24px",
                border: "1px solid #e0f2fe",
                boxShadow: "0 4px 15px rgba(0,0,0,0.04)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: isMobile ? "center" : "flex-start",
                  gap: "16px",
                  flexDirection: isMobile ? "column" : "row",
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                    width: "52px",
                    height: "52px",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <FiTarget size={24} color="white" />
                </div>
                <div>
                  <h3
                    style={{
                      fontSize: isMobile ? "1.25rem" : "1.5rem",
                      fontWeight: "700",
                      color: "#0f172a",
                      marginBottom: "8px",
                    }}
                  >
                    Misyonumuz
                  </h3>
                  <p
                    style={{
                      color: "#475569",
                      lineHeight: "1.7",
                      fontSize: isMobile ? "0.95rem" : "1.05rem",
                      margin: 0,
                    }}
                  >
                    Çocukların bireysel gelişim özelliklerini göz önünde
                    bulundurarak zihinsel, duygusal, sosyal, motor ve öz bakım
                    becerilerini desteklemek; ailelere rehberlik ederek
                    çocuk–aile iletişimini güçlendirmek.
                  </p>
                </div>
              </div>
            </div>

            {/* Vizyon Kartı */}
            <div
              style={{
                background: "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
                borderRadius: "16px",
                padding: isMobile ? "20px" : "24px",
                border: "1px solid #dcfce7",
                boxShadow: "0 4px 15px rgba(0,0,0,0.04)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: isMobile ? "center" : "flex-start",
                  gap: "16px",
                  flexDirection: isMobile ? "column" : "row",
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #10b981, #059669)",
                    width: "52px",
                    height: "52px",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <FiEye size={24} color="white" />
                </div>
                <div>
                  <h3
                    style={{
                      fontSize: isMobile ? "1.25rem" : "1.5rem",
                      fontWeight: "700",
                      color: "#0f172a",
                      marginBottom: "8px",
                    }}
                  >
                    Vizyonumuz
                  </h3>
                  <p
                    style={{
                      color: "#475569",
                      lineHeight: "1.7",
                      fontSize: isMobile ? "0.95rem" : "1.05rem",
                      margin: 0,
                    }}
                  >
                    Her çocuğun potansiyelini keşfederek özgüvenli, mutlu ve
                    sağlıklı bireyler olarak yetişmesine katkı sunmak; alanında
                    güvenilir, yenilikçi ve örnek bir danışmanlık merkezi olmak.
                  </p>
                </div>
              </div>
            </div>

            {/* Neden Bizi Seçmelisiniz - Misyon/Vizyon altında */}
            <div
              style={{
                background: "linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)",
                borderRadius: "16px",
                padding: isMobile ? "20px" : "24px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <h4
                style={{
                  fontSize: isMobile ? "1.1rem" : "1.25rem",
                  fontWeight: "700",
                  color: "white",
                  marginBottom: "16px",
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                Neden Bizi Seçmelisiniz?
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "10px",
                }}
              >
                {[...whyList, ...why2List].map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#3b82f6",
                        borderRadius: "6px",
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <FiCheck size={14} color="white" />
                    </div>
                    <span
                      style={{
                        color: "white",
                        fontSize: isMobile ? "0.85rem" : "0.9rem",
                        fontWeight: "500",
                      }}
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* İstatistikler */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
            gap: isMobile ? "12px" : "16px",
          }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              style={{
                backgroundColor: "white",
                borderRadius: isMobile ? "16px" : "20px",
                padding: isMobile ? "20px 12px" : "24px 16px",
                textAlign: "center",
                boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
                border: "1px solid #f1f5f9",
              }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div
                style={{
                  backgroundColor: `${stat.color}15`,
                  width: isMobile ? "50px" : "60px",
                  height: isMobile ? "50px" : "60px",
                  borderRadius: isMobile ? "12px" : "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                }}
              >
                <stat.icon size={isMobile ? 24 : 28} color={stat.color} />
              </div>
              <p
                style={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  fontWeight: "800",
                  color: "#0f172a",
                  marginBottom: "2px",
                  lineHeight: 1,
                }}
              >
                {stat.value}
              </p>
              <p
                style={{
                  color: "#64748b",
                  fontWeight: "500",
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  margin: 0,
                }}
              >
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
